import React, { ChangeEvent, DragEvent, useState } from 'react'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { CloudUploadIcon, RemoveAttachmentIcon } from '../../assets/icons'
import FileIcon from '../../assets/icons/files'
import {
  FileExtension,
  getFileExtension,
  getMaxFileSize,
} from '../../utils/files'
import { Grid } from '@mui/material'

let customError: string | undefined

const styles = ({
  isDarkMode = false,
  isOverlayVisible = false,
  overlayZIndex = 2,
}) => ({
  label: {
    textAlign: 'left',
    width: '100%',
    fontSize: '12px',
    lineHeight: '16px',
    color: isDarkMode ? '#FFFFFF' : '#5d5d5d',
    marginBottom: '8px',
  },
  mainContainer: {
    ' .dragAndDropLabel-text': {
      display: 'flex',
      fontSize: '14px',
      lineHeight: '16px',
    },

    ' .cloud-upload-icon': {
      fill: '#566983',
      height: '16px',
      lineHeight: '16px',
      margin: '3px 4px 0 0',
      width: '24px',
    },

    ' .maximum-legend': {
      color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
      fontSize: '11px',
      lineHeight: '16px',
      marginTop: '8px',
      display: 'block',
      textAlign: 'center',
      width: '100%',
    },

    ' .dragAndDropLabel': {
      display: 'flex',
    },

    '&.disabled': {
      label: {
        backgroundColor: '#d7dce2',
        borderColor: '#a2a8b0',
        color: '#a7b3c2',
        cursor: 'default',
      },

      '.maximum-legend': {
        color: '#a7b3c2',
      },
    },
  },
  dropZone: {
    alignItems: 'center',
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#0000000D',
    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : '#00000033',
    boxShadow: '0px 2px 4px 0px #00000012',
    borderRadius: '4px',
    borderStyle: 'dashed',
    borderWidth: '1px',
    color: isDarkMode ? '#FFFFFF' : '#566983',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    padding: '28px',
    position: 'relative',
    textAlign: 'center',
    zIndex: 1,
    pointerEvents: isOverlayVisible ? 'none' : 'auto',
  },
  fileInput: {
    display: 'none',
    padding: 0,
    height: '100%',
    backgroundColor: 'red',
    opacity: 0,
  },
  fileList: {
    borderColor: '#56698380',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    color: '#566983',
    listStyle: 'none',
    margin: 0,
    position: 'relative',
    padding: 0,
    top: '1px',
    width: '100%',
  },
  fileListItem: {
    borderBottom: '1px solid #56698380',
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    fontSize: '16px',
    color: 'rgba(0, 0, 0, 0.5)',
  },
  fileIcon: {
    width: '50px',
    height: '36px',
    margin: '0 0 0 18px',
  },
  removeAttachmentBtn: {
    borderRadius: '14px',
    display: 'flex',
    height: '28px',
    lineHeight: '28px',
    marginLeft: 'auto',
    marginRight: '8px',
    minWidth: '28px',
    padding: 0,
    width: '28px',
  },
  removeAttachmentIcon: {
    width: '14px',
    height: '14px',
    padding: 0,
  },
  error: {
    color: '#d32f2f',
    fontSize: '12px',
    lineHeight: '20px',
    textAlign: 'left',
    textIndent: '14px',
    width: '100%',
  },
  overlay: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isOverlayVisible
      ? 'rgba(36, 46, 62, 0.9);'
      : 'rgba(0,0,0,0)' /* Black background with opacity */,
    pointerEvents: 'none',
    zIndex: overlayZIndex,
    ' .cloud-upload-icon-overlay': {
      paddingTop: '16px',
      marginRight: '22px',
      visibility: isOverlayVisible ? 'visible' : 'hidden',
      pointerEvents: 'none',
    },
  },
  innerOverlay: {
    border: isOverlayVisible ? '8px dashed rgba(175, 187, 206, 1)' : 0,
    borderRadius: '4px',
    margin: '24px',
    height: 'calc(100vh - 60px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isOverlayVisible ? 'rgba(203, 214, 230, 1)' : 'rgba(0,0,0,0)',
  },
  overlayText: {
    fontWeight: 325,
    fontSize: '32px',
    lineHeight: '40px',
    pointerEvents: 'none',
  },
  overlayIcon: {
    width: 48,
    height: 32,
  },
  eventOverlay: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
})

const getFilesToUpload = (
  files: FileList,
  currentFiles: Array<FilePropsType>,
  allowedFileExtensions?: Array<string>,
  maxFileNumber?: number,
  maxFileSize?: number,
  onError?: Function,
): Array<FilePropsType> => {
  if (!files || files.length < 1) {
    return []
  }

  if (maxFileNumber && files.length + currentFiles.length > maxFileNumber) {
    const error = `Sorry, you cannot upload more than ${maxFileNumber} files`
    customError = error

    if (onError) {
      onError(error)
    }
    throw new Error(error)
  }

  const validatedFiles = new Array<File>()

  Array.from(files).forEach((file: File) => {
    const fileExtension = getFileExtension(file?.name)
    if (
      !fileExtension ||
      !allowedFileExtensions?.includes(fileExtension as string)
    ) {
      const error = `Sorry, only ${allowedFileExtensions?.join(
        ', ',
      )} extensions can be uploaded`
      customError = error

      if (onError) {
        onError(error)
      }
      throw new Error(error)
    }

    if (maxFileSize && file.size > getMaxFileSize(maxFileSize)) {
      const error = `Sorry, uploads cannot exceed ${maxFileSize}mb`
      customError = error

      if (onError) {
        onError(error)
      }
      throw new Error(error)
    }

    validatedFiles.push(file)
  })
  const currentFilesNames = currentFiles.map(({ name }) => name)
  const filesToUpload = validatedFiles.filter(
    ({ name }) => !currentFilesNames.includes(name),
  )

  customError = undefined

  return filesToUpload.map((file: File) => {
    const extension = getFileExtension(file?.name)
    if (!extension) {
      throw new Error(`invalid extension for ${file?.name}`)
    }
    return {
      extension,
      file,
      name: file?.name,
    }
  })
}

interface FilePropsType {
  extension: FileExtension
  file: File
  name: string
}

export interface FileUploaderProps {
  allowedFileExtensions?: Array<string>
  disabled?: boolean
  dragAndDropLabel?: string
  form?: UseFormReturn
  handleFileDrop?: Function
  id?: string
  isDarkMode?: boolean
  label?: string
  maxFileNumber?: number
  maxFileSize?: number
  name?: string
  onError?: Function
  overlayText?: string
  required?: boolean
  rules?: object
  showFullScreenOverlay?: boolean
  showFileList?: boolean
  uploadingFile?: boolean
  handleEnterOverlay?: Function
  handleLeaveOverlay?: Function
  overlayZIndex?: number
}

const FileUploader = ({
  allowedFileExtensions,
  disabled,
  dragAndDropLabel,
  form,
  handleFileDrop,
  id,
  isDarkMode,
  label,
  maxFileNumber,
  maxFileSize,
  name,
  onError,
  overlayText,
  required,
  rules,
  showFileList,
  showFullScreenOverlay,
  uploadingFile,
  handleEnterOverlay,
  handleLeaveOverlay,
  overlayZIndex,
}: FileUploaderProps) => {
  const [currentFiles, setCurrentFiles] = useState<Array<FilePropsType>>([])
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const defaultForm = form || useForm()
  const {
    formState: { errors },
  } = defaultForm

  const defaultSx = styles({ isDarkMode, isOverlayVisible, overlayZIndex })
  const fileUploaderName = name as string

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer || disabled || !maxFileNumber || !maxFileSize) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const filesToLoad = getFilesToUpload(
      event.dataTransfer.files,
      currentFiles,
      allowedFileExtensions,
      maxFileNumber,
      maxFileSize,
      onError,
    )

    setCurrentFiles(existingFiles => [...existingFiles, ...filesToLoad])
  }

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (
      !event.target ||
      disabled ||
      !maxFileNumber ||
      !event.target.files ||
      !maxFileSize
    ) {
      return
    }

    event.preventDefault()

    const filesToUpload = getFilesToUpload(
      event?.target?.files,
      currentFiles,
      allowedFileExtensions,
      maxFileNumber,
      maxFileSize,
      onError,
    )

    setCurrentFiles(existingFiles => [...existingFiles, ...filesToUpload])
  }

  const handleRemove = (fileName: string) => {
    const remainingFiles = currentFiles.filter(
      (currentFile: FilePropsType) => currentFile?.name !== fileName,
    )
    setCurrentFiles(remainingFiles)
  }

  const handleDragEnterOverlay = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOverlayVisible(true)
    if (typeof handleEnterOverlay === 'function') {
      handleEnterOverlay()
    }
  }

  const handleDragLeaveOverlay = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOverlayVisible(false)
    if (typeof handleLeaveOverlay === 'function') {
      handleLeaveOverlay()
    }
  }

  const handleDragOver = (
    event: DragEvent<HTMLDivElement | HTMLInputElement>,
  ) => {
    event.preventDefault()
  }

  const handleFileDropDefault = (
    event: DragEvent<HTMLDivElement | HTMLInputElement>,
    onChange: Function,
  ) => {
    return onChange(
      [event?.dataTransfer?.files, setCurrentFiles],
      handleDrop(event),
    )
  }

  const handleFileInputChangeDefault = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: Function,
  ) => {
    return onChange([event.target?.files, setCurrentFiles], handleChange(event))
  }

  const handleFileDropOverlay = (
    event: DragEvent<HTMLDivElement>,
    onChange: Function,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    isOverlayVisible && setIsOverlayVisible(false)
    if (typeof handleFileDrop === 'function') handleFileDrop(event)
    else return handleFileDropDefault(event, onChange)
  }

  const maxFileNumberDisplayText =
    maxFileNumber === 1
      ? 'Maximum 1 file per upload. '
      : `Upload up to ${maxFileNumber} files `

  return (
    <Controller
      control={defaultForm.control}
      name={fileUploaderName}
      rules={rules}
      render={props => (
        <>
          <Box
            className={disabled ? 'disabled' : ''}
            sx={defaultSx.mainContainer}
          >
            {label && (
              <Typography sx={defaultSx.label}>
                {label} ({required ? 'Required' : 'Optional'})
              </Typography>
            )}
            {showFileList && currentFiles?.length > 0 && (
              <Box component='ul' sx={defaultSx.fileList}>
                {currentFiles.map(({ extension, name }: FilePropsType) => {
                  return (
                    <Box component='li' key={name} sx={defaultSx.fileListItem}>
                      <FileIcon
                        extension={extension}
                        iconProps={defaultSx.fileIcon}
                      />
                      <Box component='span'>{name}</Box>
                      <Button
                        color='primary'
                        onClick={() => handleRemove(name)}
                        style={defaultSx.removeAttachmentBtn}
                      >
                        <RemoveAttachmentIcon
                          {...defaultSx.removeAttachmentIcon}
                        />
                      </Button>
                    </Box>
                  )
                })}
              </Box>
            )}
            <Box
              component='label'
              onDragOver={handleDragOver}
              onDrop={
                typeof handleFileDrop === 'function'
                  ? event => handleFileDrop(event)
                  : event =>
                      handleFileDropDefault(event, props?.field?.onChange)
              }
              sx={defaultSx.dropZone}
            >
              <Box
                accept={allowedFileExtensions
                  ?.map((afe: string) => `.${afe}`)
                  .join(',')}
                component='input'
                disabled={disabled}
                id={id}
                multiple
                onChange={
                  typeof handleFileDrop === 'function'
                    ? (event: React.ChangeEvent<HTMLInputElement>) =>
                        handleFileDrop(event)
                    : (event: React.ChangeEvent<HTMLInputElement>) =>
                        handleFileInputChangeDefault(
                          event,
                          props?.field?.onChange,
                        )
                }
                sx={defaultSx.fileInput}
                type='file'
                value={props?.field?.value?.filename}
                {...props}
              />
              {uploadingFile && (
                <CircularProgress
                  sx={{
                    color: '#566983',
                    marginBottom: '20px',
                  }}
                />
              )}
              <Grid flexDirection='column' alignItems='center'>
                <CloudUploadIcon
                  path={isDarkMode ? { fill: '#ffffff' } : undefined}
                />
                <Typography
                  sx={{
                    color: '#000000B2',
                    fontWeight: 400,
                    fontSize: '0.75rem',
                    lineHeight: 2,
                  }}
                >
                  {dragAndDropLabel}
                </Typography>
                <Typography
                  sx={{
                    color: '#00000080',
                    fontWeight: 400,
                    fontSize: '0.5rem',
                    lineHeight: 1.75,
                  }}
                >
                  {`Supports ${allowedFileExtensions?.join(', ')}`}
                </Typography>
                <Typography
                  sx={{
                    color: '#00000080',
                    fontWeight: 400,
                    fontSize: '0.5rem',
                    lineHeight: 1.75,
                  }}
                >
                  {`${maxFileNumberDisplayText}, ${maxFileSize}mb each`}
                </Typography>
              </Grid>
              <Box className='dragAndDropLabel'></Box>
            </Box>
            {errors?.[fileUploaderName] && (
              <Typography sx={defaultSx.error}>
                {`${errors?.[fileUploaderName]?.message}`}
              </Typography>
            )}
            {customError && (
              <Typography sx={defaultSx.error}>{customError}</Typography>
            )}
          </Box>
          {showFullScreenOverlay && (
            <>
              <Box sx={defaultSx.overlay}>
                <Box sx={defaultSx.innerOverlay}>
                  <CloudUploadIcon
                    className='cloud-upload-icon-overlay'
                    width={'48'}
                    height={'32'}
                    path={{ fill: 'rgba(175, 187, 206, 1)' }}
                  />
                  <Typography sx={defaultSx.overlayText} component='span'>
                    {overlayText}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={defaultSx.eventOverlay}
                onDragEnter={handleDragEnterOverlay}
                onDragLeave={handleDragLeaveOverlay}
                onDragOver={handleDragOver}
                onDrop={event =>
                  handleFileDropOverlay(event, props?.field?.onChange)
                }
              />
            </>
          )}
        </>
      )}
    />
  )
}

FileUploader.defaultProps = {
  allowedFileExtensions: [
    'bmp',
    'csv',
    'doc',
    'docx',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'pdf',
    'xls',
    'xlsx',
  ],
  disabled: false,
  dragAndDropLabel: 'Drag & Drop or Click to Browse',
  handleFileDrop: undefined,
  id: 'fileUploader',
  isDarkMode: false,
  maxFileNumber: 20,
  maxFileSize: 50,
  name: 'fileUploader',
  onError: (error: string) => console.log('error', error),
  overlayText: 'drop file',
  required: false,
  showFileList: true,
  showFullScreenOverlay: false,
  handleEnterOverlay: undefined,
  handleLeaveOverlay: undefined,
  overlayZIndex: 2,
}

export default FileUploader
